import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { useEffect, useRef } from 'react'
import { NotFound } from 'secondstep-components'
import dayjs from 'dayjs'

import { resolveRouteServiceApp } from 'utils/productHelpers'

import { RouteContentGetter } from 'layers/content/v2/Hocs/RouteContentGetter'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import { CourseManagerGetter } from 'layers/content/v2/Hocs/CourseManagerGetter'
import NavigationHelper from 'layers/navigation/navigationHelper'

import K8TeachComponent from './component'
import {
  backToLessonsHandlerFactory,
  unitSelectHandlerFactory,
} from './handlers'

import { extractUnitSlugsFromTabSummaries } from './data'

import {
  selectCurrentClassInstanceData,
  parseGradeAndProgram,
  getCourseLinks,
  coursesWithNewContentfulIds,
} from 'utils/lmsHelpers'

const MILISECONDS_BEFORE_REFRESH = 15000

export const K8TeachContainer = props => {
  const {
    location,
    match,
    navigationEvent,
    userContext,
    content: courseContent,
    productTabTitle,
    instructorData,
    courseManager,
    fetchInstructorData,
  } = props || {}
  const lastTimeVisible = useRef(null)

  const visibilityHandler = async () => {
    const timeNow = dayjs().valueOf()
    const timeSinceLastVisible = timeNow - lastTimeVisible.current
    const debounceTimeLongEnough =
      timeSinceLastVisible >= MILISECONDS_BEFORE_REFRESH

    if (
      lastTimeVisible.current &&
      debounceTimeLongEnough &&
      document.visibilityState === 'visible'
    ) {
      await fetchInstructorData()
    }
    lastTimeVisible.current = timeNow
  }

  useEffect(() => {
    document.addEventListener('visibilitychange', visibilityHandler)
    return () => {
      document.removeEventListener('visibilitychange', visibilityHandler)
    }
  }, [])

  const { ourClass, classId } = selectCurrentClassInstanceData({
    instructorData,
    courseManager,
    match,
    userContext,
    location,
  })
  // Extract the unit slugs from the tab summaries
  const { data } = courseContent ?? {}
  const { courseLevelContent } = data ?? {}
  const { tabSummaries } = courseLevelContent ?? {}
  const unitSlugs = extractUnitSlugsFromTabSummaries(tabSummaries)

  // Get the product name and the course links
  const { program, programKey: productName } = parseGradeAndProgram(match?.url)
  const courseLinks = getCourseLinks(
    coursesWithNewContentfulIds({ programType: program }),
  )

  const isTeach = match?.params?.verb === 'teach'

  // TODO LEARN-20166: this is gating against a race condition where we dont get the courses in course manager back fast enough to get classes. Probably need to check loading state of course manager somewhere
  const ClassNotFound = <NotFound location={location} match={match} />

  if (!ourClass && isTeach) {
    return ClassNotFound
  }

  return (
    <K8TeachComponent
      classId={classId}
      courseLinks={courseLinks}
      isTeach={isTeach}
      location={location}
      match={match}
      onBacklinkClick={backToLessonsHandlerFactory(
        productName,
        navigationEvent,
      )}
      onUnitSelect={unitSelectHandlerFactory(
        productName,
        navigationEvent,
        location,
      )}
      ourClass={ourClass}
      productName={productName}
      productTabTitle={productTabTitle}
      unitSlugs={unitSlugs}
    />
  )
}

const mapStateToProps = state => {
  const { userContextManager, productManager } = state

  return {
    userContext: userContextManager,
    productTabTitle: productManager?.productMap?.name || '',
  }
}

const mapDispatchToPropsCombiner = {
  ...NavigationHelper.mapDispatchToProps,
}

export default withSiteContext(
  CourseManagerGetter(
    RouteContentGetter(
      withRouter(
        connect(mapStateToProps, mapDispatchToPropsCombiner)(K8TeachContainer),
      ),
      {
        app: ({ route }) => resolveRouteServiceApp(route),
      },
    ),
  ),
)

K8TeachContainer.propTypes = {
  content: PropTypes.object,
  fetchInstructorData: PropTypes.func,
  instructorData: PropTypes.object,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    params: PropTypes.shape({
      productName: PropTypes.string,
      verb: PropTypes.string,
    }),
  }),
  navigationEvent: PropTypes.func,
  productTabTitle: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      pageData: PropTypes.shape({
        route: PropTypes.string,
      }),
    }),
  ),
  userContext: PropTypes.object,
}
